import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs, { breadcrumbLd } from "@/components/Breadcrumbs";
import Icon from "@/components/Icon";
import { Btn } from "@/components/Btn";
import { Reveal, Stagger, item } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getService, servicePath, JUNK_SPOKES, TOP_SERVICES } from "@/lib/services";
import { SERVICE_CONTENT } from "@/content/servicesContent";
import { CITIES, cityPath } from "@/lib/cities";
import { BRAND, CTA } from "@/lib/brand";
import NotFound from "@/pages/NotFound";

const STEPS = [
  { n: "01", t: "Send it over", d: "Call, text or fill out the quick form. Tell us what needs to go and snap a photo if you like." },
  { n: "02", t: "Pick a time", d: "We give you a clear arrival window, often same day. No waiting around all day." },
  { n: "03", t: "Poof. Gone.", d: "Our crew loads it, sweeps up and skedaddles. Your space is yours again." },
];

export default function ServicePage() {
  const params = useParams();
  const slug = params.spoke || params.slug;
  const svc = getService(slug);
  const content = SERVICE_CONTENT[slug];
  if (!svc || !content) return <NotFound />;

  const isPillar = content.isPillar;
  const crumbs = isPillar || !params.spoke
    ? [{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: svc.title }]
    : [{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: "Junk Removal", to: "/services/junk-removal" }, { label: svc.title }];

  const related = TOP_SERVICES.filter((s) => s.slug !== slug).slice(0, 4);
  const serviceLd = {
    "@context": "https://schema.org", "@type": "Service", name: content.metaTitle,
    provider: { "@type": "HomeAndConstructionBusiness", name: "Skedaddle Junk Removal", telephone: "+1-843-518-0021" },
    areaServed: "Charleston, Berkeley and Dorchester counties, SC", description: content.metaDescription,
  };

  return (
    <>
      <Seo path={servicePath(svc)} title={content.metaTitle} description={content.metaDescription}
        jsonLd={[serviceLd, breadcrumbLd(crumbs)]} />

      {/* Hero */}
      <section className="bg-white border-b-2 border-charcoal py-12 md:py-16">
        <div className="mx-auto max-w-5xl px-4 md:px-8">
          <Breadcrumbs items={crumbs} />
          <div className="mt-6 flex items-start gap-5">
            <div className="hidden sm:flex w-20 h-20 rounded-2xl border-2 border-charcoal bg-sunny items-center justify-center shrink-0"><Icon name={svc.icon} className="w-10 h-10" /></div>
            <div>
              <h1 className="font-heading font-bold text-4xl md:text-6xl tracking-tight text-charcoal">{svc.title}</h1>
              <p className="mt-4 font-body text-lg md:text-xl text-charcoal/75 max-w-2xl">{content.intro[0]}</p>
            </div>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <Btn to="/contact" variant="primary" data-testid="service-quote-btn">{CTA.quote}</Btn>
            <Btn href={BRAND.phoneHref} variant="secondary"><Phone className="w-5 h-5" /> {BRAND.phoneDisplay}</Btn>
          </div>
        </div>
      </section>

      {/* Intro + Included */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-4 md:px-8 grid md:grid-cols-2 gap-10">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Here is how we help</h2>
            <div className="mt-4 space-y-4 font-body text-lg text-charcoal/75">
              {content.intro.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-cream border-2 border-charcoal rounded-3xl p-7 shadow-brut">
              <h3 className="font-heading font-bold text-2xl">What we take</h3>
              <ul className="mt-4 space-y-3">
                {content.included.map((it) => (
                  <li key={it} className="flex items-center gap-3 font-body"><span className="w-6 h-6 rounded-full bg-teal text-cream flex items-center justify-center shrink-0"><Check className="w-4 h-4" /></span>{it}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Spokes (pillar only) */}
      {isPillar && (
        <section className="bg-cream py-16 md:py-20 border-y-2 border-charcoal">
          <div className="mx-auto max-w-6xl px-4 md:px-8">
            <Reveal><h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Ways we make junk skedaddle</h2>
              <p className="mt-3 font-body text-lg text-charcoal/70 max-w-2xl">However much you have got, we have a way to haul it.</p></Reveal>
            <Stagger className="mt-10 grid gap-5 sm:grid-cols-2">
              {JUNK_SPOKES.map((s) => (
                <motion.div key={s.slug} variants={item}>
                  <Link to={servicePath(s)} data-testid={`spoke-card-${s.slug}`}
                    className="group block h-full bg-white border-2 border-charcoal rounded-3xl p-7 shadow-brut hover:-translate-y-1 hover:shadow-brutlg transition-[transform,box-shadow]">
                    <div className="w-14 h-14 rounded-2xl border-2 border-charcoal bg-teal text-cream flex items-center justify-center mb-4"><Icon name={s.icon} className="w-7 h-7" /></div>
                    <h3 className="font-heading font-bold text-2xl">{s.title}</h3>
                    <p className="mt-2 font-body text-charcoal/70">{s.blurb}</p>
                    <span className="mt-4 inline-flex items-center gap-1 font-body font-bold text-coral">Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                  </Link>
                </motion.div>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="bg-charcoal text-cream py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <Reveal><h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">How it works</h2></Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <Reveal key={s.n}><div className="border-2 border-cream/20 rounded-3xl p-7 bg-cream/5">
                <span className="font-heading font-bold text-5xl text-sunny">{s.n}</span>
                <h3 className="font-heading font-bold text-2xl mt-3">{s.t}</h3>
                <p className="mt-2 font-body text-cream/70">{s.d}</p>
              </div></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why + FAQ */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-4 md:px-8 grid md:grid-cols-2 gap-10">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Why folks call Skedaddle</h2>
            <ul className="mt-5 space-y-3">
              {content.why.map((w) => (
                <li key={w} className="flex items-start gap-3 font-body text-lg"><span className="w-6 h-6 mt-1 rounded-full bg-coral text-cream flex items-center justify-center shrink-0"><Check className="w-4 h-4" /></span>{w}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Quick questions</h2>
            <Accordion type="single" collapsible className="mt-5 space-y-3">
              {content.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} className="bg-cream border-2 border-charcoal rounded-2xl px-5 shadow-brutsm">
                  <AccordionTrigger className="font-heading font-bold text-base text-left hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="font-body text-charcoal/75">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Related services */}
      <section className="bg-cream py-16 md:py-20 border-y-2 border-charcoal">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <h2 className="font-heading font-bold text-3xl tracking-tight">More ways we can help</h2>
          <div className="mt-8 flex flex-wrap gap-3">
            {related.map((s) => (
              <Link key={s.slug} to={servicePath(s)} className="inline-flex items-center gap-2 bg-white border-2 border-charcoal rounded-full px-4 py-2 font-body font-bold shadow-brutsm hover:bg-sunny transition-colors">
                <Icon name={s.icon} className="w-4 h-4" /> {s.title}
              </Link>
            ))}
          </div>
          <h3 className="font-heading font-bold text-xl mt-10 mb-4">Serving these Lowcountry towns</h3>
          <div className="flex flex-wrap gap-2">
            {CITIES.slice(0, 12).map((c) => (
              <Link key={c.slug} to={cityPath(c)} className="text-sm font-body font-semibold underline decoration-2 decoration-teal underline-offset-4 hover:text-coral">{c.name}</Link>
            ))}
            <Link to="/service-areas" className="text-sm font-body font-bold text-coral">and more</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-coral text-cream py-16 md:py-20 text-center">
        <div className="mx-auto max-w-2xl px-4 md:px-8">
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">Ready to make it skedaddle?</h2>
          <p className="mt-3 font-body text-lg text-cream/90">Free quotes, friendly crew, no pressure. One call and it is gone!</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Btn to="/contact" variant="accent">{CTA.quote}</Btn>
            <Btn href={BRAND.smsHref} variant="white">Text us</Btn>
          </div>
        </div>
      </section>
    </>
  );
}
